/**
 * StopRateLedgerCard · v2.7.65+E171
 * ─────────────────────────────────────────────────────────────────────────
 * Compact counter widget on the Admin Overview showing "opt-outs in the
 * last 30 days" plus a mini bar-chart of the daily trend. Backed by
 * GET /api/admin/sms/stop-rate.
 *
 * The whole card is a click-through: taps the Consent Audit view filtered
 * to sms_consent_source=twilio_stop_reply so a supervisor can immediately
 * see which numbers dropped off. Any big spike here should trigger an
 * outbound-content audit.
 *
 * Read-only. No Twilio, no SMTP. Polls every 60s (opt-outs are not
 * high-frequency).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { MessageSquareOff, TrendingDown, TrendingUp, Minus, ArrowRight, Loader2 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { useNavigate } from "react-router-dom";

const POLL_MS = 60_000;

function TrendPill({ delta }) {
  if (delta === null || delta === undefined) return (
    <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px]" data-testid="stop-rate-trend-none">
      <Minus className="h-2.5 w-2.5 mr-1"/>New baseline
    </Badge>
  );
  if (delta > 0) return (
    <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/40 font-bold text-[10px]" data-testid="stop-rate-trend-up">
      <TrendingUp className="h-2.5 w-2.5 mr-1"/>+{delta.toFixed(1)}% vs prior 30d
    </Badge>
  );
  if (delta < 0) return (
    <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 font-bold text-[10px]" data-testid="stop-rate-trend-down">
      <TrendingDown className="h-2.5 w-2.5 mr-1"/>{delta.toFixed(1)}% vs prior 30d
    </Badge>
  );
  return (
    <Badge variant="outline" className="border-zinc-600 text-zinc-300 text-[10px]" data-testid="stop-rate-trend-flat">
      <Minus className="h-2.5 w-2.5 mr-1"/>0.0% vs prior 30d
    </Badge>
  );
}

export default function StopRateLedgerCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data: resp } = await adminApi.get("/admin/sms/stop-rate");
      setData(resp);
    } catch (err) {
      console.warn("stop-rate load failed", err?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Peak of the daily series drives the sparkline scaling.
  const peak = useMemo(() => {
    if (!data?.series_30d?.length) return 1;
    return Math.max(1, ...data.series_30d.map((d) => d.count || 0));
  }, [data]);

  const goToDetail = () => {
    navigate("/admin#tab=user-mgmt&sub=users-consent-audit&source=twilio_stop_reply");
  };

  // ── Spike flag: >2× prior-30d OR ≥3 in the last 24h. Anything over that
  //    lights up rose so it can't be missed on the Overview.
  const isSpike = data && (
    (data.prev_30d > 0 && data.last_30d > data.prev_30d * 2)
    || (data.last_24h >= 3)
  );

  return (
    <Card
      className={`p-4 border transition ${isSpike ? "bg-rose-950/40 border-rose-500/50" : "bg-zinc-900 border-zinc-800"} hover:border-amber-400/50 cursor-pointer`}
      onClick={goToDetail}
      data-testid="stop-rate-card"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToDetail(); } }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <MessageSquareOff className={`h-4 w-4 ${isSpike ? "text-rose-300" : "text-amber-300"}`}/>
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-400">
              SMS Opt-outs · Last 30 days
            </div>
          </div>
          {loading && !data ? (
            <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500" data-testid="stop-rate-loading">
              <Loader2 className="h-3.5 w-3.5 animate-spin"/>Loading…
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-3 mt-1">
                <div className="meter-display text-3xl font-extrabold text-zinc-100" data-testid="stop-rate-count-30d">
                  {(data?.last_30d ?? 0).toLocaleString()}
                </div>
                <div className="text-[11px] text-zinc-500">
                  <span className="text-zinc-300 font-bold" data-testid="stop-rate-count-24h">{data?.last_24h ?? 0}</span> today
                  {" · "}
                  <span className="text-zinc-300 font-bold" data-testid="stop-rate-count-7d">{data?.last_7d ?? 0}</span> this week
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <TrendPill delta={data?.delta_pct}/>
                {isSpike && (
                  <Badge className="bg-rose-500/20 text-rose-200 border border-rose-500/60 font-bold text-[10px]" data-testid="stop-rate-spike-flag">
                    Spike — review outbound content
                  </Badge>
                )}
              </div>
            </>
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-zinc-500 shrink-0 mt-0.5" data-testid="stop-rate-jump"/>
      </div>

      {/* Sparkline — 30 daily bars, rose-tinted, newest on the right. */}
      {data?.series_30d?.length ? (
        <div className="mt-3 flex items-end gap-[2px] h-10" data-testid="stop-rate-sparkline">
          {data.series_30d.map((d) => {
            const pct = peak > 0 ? Math.max(4, Math.round((d.count / peak) * 100)) : 4;
            return (
              <div
                key={d.date}
                className={`flex-1 rounded-sm ${d.count > 0 ? "bg-rose-400/70" : "bg-zinc-800"}`}
                style={{ height: `${pct}%` }}
                title={`${d.date} · ${d.count} opt-out${d.count === 1 ? "" : "s"}`}
                data-testid={`stop-rate-day-${d.date}`}
              />
            );
          })}
        </div>
      ) : null}

      <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-500">
        <span>Total all-time: <span className="text-zinc-300 font-mono">{(data?.total_all_time ?? 0).toLocaleString()}</span></span>
        <span className="uppercase tracking-widest">Tap to audit</span>
      </div>
    </Card>
  );
}
